
import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import { isFavoritesCacheFresh, readFavoritesCache, writeFavoritesCache } from '../utils/favoritesCache'
import { isRateLimitError } from '../utils/rateLimit'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft, Heart, Gift} from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import LoginModal from '../components/LoginModal'
import MemberSidebar from '../components/MemberSidebar'

const CartPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const { items, total, itemCount, updateQuantity, removeItem, clearCart, addItem } = useCart()
  const [isClearing, setIsClearing] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [hasShownModal, setHasShownModal] = useState(false)

  // 檢查登入狀態，避免重複彈出
  useEffect(() => {
    const currentUser = lumi.auth.user
    if (!currentUser && items.length > 0 && !hasShownModal) {
      setIsLoginModalOpen(true)
      setHasShownModal(true)
    }
  }, [items, hasShownModal])

  // 監聽登入狀態變化
  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange((newUser) => {
      setUser(newUser)
      if (newUser) {
        setIsLoginModalOpen(false)
      }
    })
    
    return () => unsubscribe()
  }, [])

  // Google 登入處理函數
  const handleGoogleSignIn = async () => {
    try {
      await lumi.auth.signIn()
    } catch (error) {
      console.error('Google sign in failed:', error)
    }
  }

  // 訪客結帳處理函數
  const handleGuestCheckout = () => {
    setIsLoginModalOpen(false)
  }

  const content = {
    zh: {
      title: '購物車',
      empty: {
        title: '您的購物車是空的',
        subtitle: '探索我們精選的咖啡豆與器具，開始您的咖啡之旅',
        cta: '開始購物'
      },
      items: {
        quantity: '數量',
        remove: '移除',
        specifications: '規格'
      },
      summary: {
        title: '訂單摘要',
        subtotal: '小計',
        shipping: '運費',
        shippingFree: '免運費',
        total: '總計',
        checkout: '前往結帳',
        continue: '繼續購物',
        clear: '清空購物車'
      },
      recommendations: {
        title: '您可能也喜歡',
        subtitle: '其他顧客也購買了這些商品'
      }
    },
    en: {
      title: 'Shopping Cart',
      empty: {
        title: 'Your cart is empty',
        subtitle: 'Explore our curated selection of coffee beans and equipment to start your coffee journey',
        cta: 'Start Shopping'
      },
      items: {
        quantity: 'Quantity',
        remove: 'Remove',
        specifications: 'Specifications'
      },
      summary: {
        title: 'Order Summary',
        subtotal: 'Subtotal',
        shipping: 'Shipping',
        shippingFree: 'Free Shipping',
        total: 'Total',
        checkout: 'Proceed to Checkout',
        continue: 'Continue Shopping',
        clear: 'Clear Cart'
      },
      recommendations: {
        title: 'You might also like',
        subtitle: 'Other customers also purchased these items'
      }
    }
  }

  const t = content[currentLang]

  const handleClearCart = async () => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    setIsClearing(true)
    setTimeout(() => {
      clearCart()
      setIsClearing(false)
    }, 500)
  }

  const formatSpecifications = (specs: any) => {
    if (!specs) return null
    
    const specLabels = {
      zh: {
        roastLevel: '烘焙程度',
        grindSize: '研磨粗細',
        packaging: '包裝'
      },
      en: {
        roastLevel: 'Roast Level',
        grindSize: 'Grind Size',
        packaging: 'Packaging'
      }
    }
    
    return Object.entries(specs)
      .filter(([, value]) => value)
      .map(([key, value]) => `${specLabels[currentLang][key] || key}: ${value}`)
      .join(' • ')
  }

  // 推薦商品（動態從資料庫獲取）
  const [recommendations, setRecommendations] = useState<any[]>([])
  const [favorites, setFavorites] = useState<string[]>([])
  const [favoriteLoading, setFavoriteLoading] = useState<string | null>(null)

  React.useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const result = await lumi.entities.products.list({
          filter: { status: 'active' },
          limit: 20,
          sort: { createdAt: -1 }
        })
        // 随机打乱并取前 3 个
        const shuffled = (result.list || []).sort(() => Math.random() - 0.5)
        setRecommendations(shuffled.slice(0, 3))
      } catch (error) {
        console.error('Failed to fetch recommendations:', error)
      }
    }
    fetchRecommendations()

    // 获取收藏列表（集中請求 + cache + 429 fallback）
    const fetchFavorites = async () => {
      const cache = readFavoritesCache()
      if (isFavoritesCacheFresh(cache, 30_000)) {
        setFavorites(cache?.productIds || [])
        return
      }

      if (lumi.auth.user?.email) {
        try {
          const result = await lumi.entities.favorites.list({
            filter: { userEmail: lumi.auth.user.email },
            limit: 1000,
          })
          const ids = (result.list || []).map((fav: any) => fav?.productId).filter(Boolean)
          writeFavoritesCache(ids)
          setFavorites(ids)
        } catch (error) {
          if (isRateLimitError(error)) {
            setFavorites(readFavoritesCache()?.productIds || [])
            return
          }
          console.error('Failed to fetch favorites:', error)
          setFavorites(readFavoritesCache()?.productIds || [])
        }
      }
    }
    fetchFavorites()
  }, [])

  const toggleFavorite = async (product: any) => {
    if (!lumi.auth.user) {
      alert(currentLang === 'zh' ? '請先登入' : 'Please login first')
      return
    }

    setFavoriteLoading(product._id)

    try {
      const isFavorited = favorites.includes(product._id)

      if (isFavorited) {
        const favList = await lumi.entities.favorites.list({
          filter: { userEmail: lumi.auth.user.email, productId: product._id }
        })
        if (favList.list && favList.list.length > 0) {
          await lumi.entities.favorites.delete(favList.list[0]._id)
        }
        setFavorites(prev => {
          const next = prev.filter(id => id !== product._id)
          writeFavoritesCache(next)
          return next
        })
      } else {
        await lumi.entities.favorites.create({
          productId: product._id,
          userEmail: lumi.auth.user.email
        })
        setFavorites(prev => {
          const next = [...prev, product._id]
          writeFavoritesCache(next)
          return next
        })
      }
    } catch (error) {
      if (!isRateLimitError(error)) {
        console.error('Failed to toggle favorite:', error)
      }
    } finally {
      setFavoriteLoading(null)
    }
  }

  if (items.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
        {/* Header Spacer */}
        <div className="pt-14"></div>
        
        {/* Empty Cart */}
        <section className="py-20">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-8">
                <ShoppingBag size={48} className="text-gray-400 dark:text-gray-500" />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-light tracking-wider text-gray-800 dark:text-gray-200 mb-4">
                {t.empty.title}
              </h1>
              
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
                {t.empty.subtitle}
              </p>
              
              <Link
                to={`/${currentLang}/shop`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="inline-flex items-center px-8 py-3 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-200"
              >
                {t.empty.cta}
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Recommendations */}
        <section className="py-20 bg-white dark:bg-gray-800 transition-colors duration-300">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-light tracking-wider text-gray-800 dark:text-gray-200 mb-4">
                {t.recommendations.title}
              </h2>
              <div className="w-16 h-px bg-gray-400 dark:bg-gray-500 mx-auto mb-6"></div>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t.recommendations.subtitle}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recommendations.map((product, index) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 group flex flex-col"
                >
                  {/* 商品圖片 */}
                  <div className="relative overflow-hidden">
                    <Link to={`/${currentLang}/shop/${product.category}/${product.productId || product._id}/${product.slug}`} className="block">
                      <img
                        src={product.images?.[0] || 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg'}
                        alt={currentLang === 'en' ? product.nameEn || product.name : product.name}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
                    </Link>
                    
                    {/* 收藏按鈕 */}
                    <button
                      onClick={() => toggleFavorite(product)}
                      disabled={favoriteLoading === product._id}
                      className="absolute top-3 right-3 p-2 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-all duration-200 hover:scale-110 disabled:opacity-50"
                    >
                      {favoriteLoading === product._id ? (
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                      ) : (
                        <Heart
                          size={20}
                          className={favorites.includes(product._id) ? 'fill-red-500 text-red-500' : 'text-gray-600 dark:text-gray-300'}
                        />
                      )}
                    </button>
                  </div>
                  
                  {/* 商品資訊 */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="mb-4">
                      <Link to={`/${currentLang}/shop/${product.category}/${product.productId || product._id}/${product.slug}`}>
                        <h3 className="text-lg font-light text-gray-800 dark:text-gray-200 mb-2 tracking-wide hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer">
                          {currentLang === 'en' ? product.nameEn || product.name : product.name}
                        </h3>
                      </Link>
                      
                      {/* 商品描述 */}
                      {(product.description || product.descriptionEn) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                          {currentLang === 'en' ? product.descriptionEn || product.description : product.description}
                        </p>
                      )}
                      
                      {/* 產區和處理法 */}
                      {(product.origin || product.specifications?.processing) && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {product.origin && (
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                              {product.origin}
                            </span>
                          )}
                          {product.specifications?.processing && (
                            <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                              {product.specifications.processing}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* 風味描述 */}
                      {product.flavorNotes && product.flavorNotes.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {product.flavorNotes.map((note: string, idx: number) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                              {note}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    
                    {/* 價格 */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xl text-gray-800 dark:text-gray-200 font-light">
                        NT$ {Number(product.price).toLocaleString('zh-TW')}
                      </span>
                    </div>
                    
                    {/* 操作按鈕區 */}
                    <div className="flex gap-2 mt-auto">
                      <Link
                        to={`/${currentLang}/shop/${product.category}/${product.productId || product._id}/${product.slug}`}
                        className="flex-1 text-center py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 text-sm leading-6"
                      >
                        {currentLang === 'zh' ? '查看詳情' : 'View Details'}
                      </Link>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          window.scrollTo({ top: 0, behavior: 'instant' })
                          const cartItem = {
                            _id: product._id,
                            name: product.name,
                            nameEn: product.nameEn,
                            price: product.price,
                            image: product.images?.[0] || 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg',
                            category: product.category
                          }
                          // 直接使用 CartContext 的 addItem
                          addItem(cartItem)
                        }}
                        className="flex-1 py-2 px-4 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-200 text-sm leading-6"
                      >
                        {currentLang === 'zh' ? '加入購物車' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 強制登入 Modal */}
        <LoginModal 
          isOpen={isLoginModalOpen}
          onClose={() => {
            setIsLoginModalOpen(false)
            // 用戶關閉 Modal 且未登入時，導航回首頁
            if (!lumi.auth.user) {
              navigate(`/${currentLang}/`)
            }
          }}
          onLogin={handleGoogleSignIn}
          onGuestCheckout={handleGuestCheckout}
          currentLang={currentLang}
        />
      </div>
    )
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
      {/* Header Spacer */}
      <div className="pt-14"></div>
      
      {/* Cart Header - 調整為與navbar相同高度 */}
      <section className="h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] h-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="h-full flex items-center justify-between"
          >
            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-light tracking-wider text-gray-800 dark:text-gray-200">
                {t.title}
              </h1>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {itemCount} {currentLang === 'zh' ? '件商品' : 'items'}
              </span>
            </div>
            
            <Link
              to={`/${currentLang}/shop`}
              className="hidden md:flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 text-sm"
            >
              <ArrowLeft size={14} className="mr-2" />
              {t.summary.continue}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className={user ? "grid grid-cols-1 lg:grid-cols-4 gap-12" : "grid grid-cols-1 lg:grid-cols-3 gap-12"}>
            {user ? (
              <div className="lg:col-span-1">
                <MemberSidebar currentLang={currentLang as "zh" | "en"} activeId="cart" />
              </div>
            ) : null}

            {/* Cart Items */}
            <div className={user ? "lg:col-span-2" : "lg:col-span-2"}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                {items.map((item, index) => (
                  <motion.div
                    key={item._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300"
                  >
                    <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-6">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <Link to={`/${currentLang}/shop/${item.category}/1001/${item._id}`}>
                          <img
                            src={item.image}
                            alt={currentLang === 'en' ? item.nameEn || item.name : item.name}
                            className="w-full md:w-24 h-48 md:h-24 object-cover hover:opacity-80 transition-opacity duration-200"
                          />
                        </Link>
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 space-y-2">
                        <Link 
                          to={`/${currentLang}/shop/${item.category}/1001/${item._id}`}
                          className="block"
                        >
                          <h3 className="text-lg font-light text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 tracking-wide">
                            {currentLang === 'en' ? item.nameEn || item.name : item.name}
                          </h3>
                        </Link>
                        
                        {item.specifications && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t.items.specifications}: {formatSpecifications(item.specifications)}
                          </p>
                        )}
                        
                        <p className="text-xl font-light text-gray-800 dark:text-gray-200">
                          NT$ {item.price.toLocaleString()}
                        </p>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400 md:hidden">
                            {t.items.quantity}:
                          </span>
                          <div className="flex items-center border border-gray-300 dark:border-gray-600">
                            <button
                              onClick={() => updateQuantity(item._id, item.quantity - 1)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={16} className={item.quantity <= 1 ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'} />
                            </button>
                            <span className="px-4 py-2 text-center min-w-[60px] text-gray-800 dark:text-gray-200">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item._id, item.quantity + 1)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                              disabled={item.quantity >= (item.maxQuantity || 999)}
                            >
                              <Plus size={16} className="text-gray-600 dark:text-gray-400" />
                            </button>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeItem(item._id)}
                          className="flex items-center text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
                        >
                          <Trash2 size={16} className="mr-1" />
                          <span className="text-sm">{t.items.remove}</span>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Clear Cart Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-8 text-center"
              >
                <button
                  onClick={handleClearCart}
                  disabled={isClearing}
                  className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200 text-sm disabled:opacity-50"
                >
                  {isClearing ? (currentLang === 'zh' ? '清空中...' : 'Clearing...') : t.summary.clear}
                </button>
              </motion.div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 sticky top-20 transition-colors duration-300"
              >
                <h2 className="text-xl font-light text-gray-800 dark:text-gray-200 mb-6 tracking-wide">
                  {t.summary.title}
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>{t.summary.subtotal}</span>
                    <span>NT$ {total.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>{t.summary.shipping}</span>
                    <span className="text-green-600 dark:text-green-400">
                      {total >= 1000 ? t.summary.shippingFree : 'NT$ 100'}
                    </span>
                  </div>
                  
                  {total < 1000 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {currentLang === 'zh' 
                        ? `滿 NT$ 1,000 免運費，還差 NT$ ${(1000 - total).toLocaleString()}`
                        : `Free shipping on orders over NT$ 1,000, need NT$ ${(1000 - total).toLocaleString()} more`
                      }
                    </div>
                  )}
                  
                  <hr className="border-gray-200 dark:border-gray-600" />
                  
                  <div className="flex justify-between text-lg font-light text-gray-800 dark:text-gray-200">
                    <span>{t.summary.total}</span>
                    <span>NT$ {(total + (total >= 1000 ? 0 : 100)).toLocaleString()}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Link
                    to={`/${currentLang}/checkout`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="w-full bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 py-3 px-6 hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-200 flex items-center justify-center"
                  >
                    {t.summary.checkout}
                    <ArrowRight size={16} className="ml-2" />
                  </Link>
                  
                  <Link
                    to={`/${currentLang}/shop`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    className="w-full border border-gray-400 dark:border-gray-500 text-gray-800 dark:text-gray-200 py-3 px-6 hover:bg-gray-800 hover:text-white dark:hover:bg-gray-200 dark:hover:text-gray-800 transition-colors duration-200 flex items-center justify-center"
                  >
                    {t.summary.continue}
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Gift size={14} className="mr-1" />
                      <span>{currentLang === 'zh' ? '精美包裝' : 'Gift Wrapping'}</span>
                    </div>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                    <div className="flex items-center">
                      <ShoppingBag size={14} className="mr-1" />
                      <span>{currentLang === 'zh' ? '安全結帳' : 'Secure Checkout'}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 強制登入 Modal */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => {
          setIsLoginModalOpen(false)
          // 用戶關閉 Modal 且未登入時，導航回首頁
          if (!lumi.auth.user) {
            navigate(`/${currentLang}/`)
          }
        }}
        onLogin={handleGoogleSignIn}
        onGuestCheckout={handleGuestCheckout}
        currentLang={currentLang}
      />
    </div>
  )
}

export default CartPage
